import React, { useState, useEffect, useCallback } from 'react';
import { Package, Search, ChevronDown, ChevronUp, Clock, Truck, CheckCircle2, XCircle, ShoppingCart, RefreshCw, User, Phone, Mail, MapPin, FileText, AlertCircle, MessageSquare, Send } from 'lucide-react';
import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

const STATUS_CONFIG = {
  new:        { label: 'Ný',          color: 'bg-blue-100 text-blue-800 border-blue-300',    icon: ShoppingCart, dotColor: 'bg-blue-500' },
  processing: { label: 'Í vinnslu',   color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock,        dotColor: 'bg-amber-500' },
  ready:      { label: 'Tilbúin',     color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, dotColor: 'bg-emerald-500' },
  delivered:  { label: 'Afhent',      color: 'bg-slate-100 text-slate-600 border-slate-300', icon: Truck,        dotColor: 'bg-slate-400' },
  cancelled:  { label: 'Hætt við',    color: 'bg-red-100 text-red-700 border-red-300',       icon: XCircle,      dotColor: 'bg-red-500' },
};

const STATUS_TABS = ['all', 'new', 'processing', 'ready', 'delivered', 'cancelled'];
const TAB_LABELS = { all: 'Allar', new: 'Nýjar', processing: 'Í vinnslu', ready: 'Tilbúnar', delivered: 'Afhentar', cancelled: 'Hætt við' };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span data-testid={`order-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
    </span>
  );
}

function OrderCard({ order, expanded, onToggle, onStatusChange, onNotify }) {
  const cust = order.customer || {};
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.new;
  const [showNotifyPopup, setShowNotifyPopup] = useState(false);

  const nextStatus = {
    new: 'processing',
    processing: 'ready',
    ready: 'delivered',
  };

  const nextLabel = {
    new: 'Hefja vinnslu',
    processing: 'Merkja tilbúna',
    ready: 'Merkja afhenta',
  };

  const handleAdvance = (orderId, targetStatus) => {
    // When advancing to "ready", show notification popup
    if (targetStatus === 'ready') {
      setShowNotifyPopup(true);
      return;
    }
    onStatusChange(orderId, targetStatus);
  };

  const handleReadyWithNotify = async (notifyType) => {
    setShowNotifyPopup(false);
    await onStatusChange(order.id, 'ready');
    if (notifyType) {
      onNotify(order.id, notifyType);
    }
  };

  return (
    <div data-testid={`order-card-${order.id}`} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-shadow hover:shadow-md">
      {/* Header row */}
      <button
        data-testid={`order-toggle-${order.id}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dotColor}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">{order.order_number}</span>
              <StatusBadge status={order.status} />
              {order.payment_status === 'paid' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300">Greitt</span>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              {cust.name}{cust.phone ? ` · ${cust.phone}` : ''} · {new Date(order.created_at).toLocaleDateString('is-IS')} kl. {new Date(order.created_at).toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">{(order.total_amount || 0).toLocaleString('is-IS')} kr.</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-700 px-5 py-4 space-y-4">
          {/* Customer info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Viðskiptavinur</h4>
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"><User className="h-3.5 w-3.5 text-slate-400" />{cust.name || '–'}</div>
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"><Phone className="h-3.5 w-3.5 text-slate-400" />{cust.phone || '–'}</div>
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"><Mail className="h-3.5 w-3.5 text-slate-400" />{cust.email || '–'}</div>
              {cust.address && <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300"><MapPin className="h-3.5 w-3.5 text-slate-400" />{cust.address}</div>}
            </div>
            {(order.notes || order.admin_notes) && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Athugasemdir</h4>
                {order.notes && <p className="text-sm text-slate-600 dark:text-slate-300"><FileText className="h-3.5 w-3.5 inline mr-1 text-slate-400" />{order.notes}</p>}
                {order.admin_notes && <p className="text-sm text-slate-600 dark:text-slate-300 italic">Admin: {order.admin_notes}</p>}
              </div>
            )}
          </div>

          {/* Items table */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Vörur</h4>
            <div className="rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                    <th className="text-left px-3 py-2">Vara</th>
                    <th className="text-center px-3 py-2">Magn</th>
                    <th className="text-right px-3 py-2">Verð</th>
                    <th className="text-right px-3 py-2">Samtals</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((li, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{li.product_name}{li.product_number && <span className="text-xs text-slate-400 ml-1">{li.product_number}</span>}{li.supplier && <span className="text-xs text-amber-500 ml-1">({li.supplier})</span>}</td>
                      <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300">{li.quantity}</td>
                      <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-300">{(li.unit_price || 0).toLocaleString('is-IS')} kr.</td>
                      <td className="px-3 py-2 text-right font-medium text-slate-900 dark:text-slate-100">{(li.total || 0).toLocaleString('is-IS')} kr.</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 dark:bg-slate-700/50 font-bold">
                    <td colSpan={3} className="px-3 py-2 text-right text-slate-700 dark:text-slate-300">Samtals:</td>
                    <td className="px-3 py-2 text-right text-slate-900 dark:text-slate-100">{(order.total_amount || 0).toLocaleString('is-IS')} kr.</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-2 flex-wrap relative">
            {nextStatus[order.status] && (
              <button
                data-testid={`order-advance-${order.id}`}
                onClick={() => handleAdvance(order.id, nextStatus[order.status])}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
              >
                {nextLabel[order.status]}
              </button>
            )}

            {/* Send notification buttons when order is already ready */}
            {order.status === 'ready' && (cust.phone || cust.email) && (
              <div className="flex items-center gap-1.5">
                {cust.phone && (
                  <button
                    data-testid={`order-notify-sms-${order.id}`}
                    onClick={() => onNotify(order.id, 'sms')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-sm font-medium transition-colors"
                  >
                    <MessageSquare className="h-4 w-4" /> SMS
                  </button>
                )}
                {cust.email && (
                  <button
                    data-testid={`order-notify-email-${order.id}`}
                    onClick={() => onNotify(order.id, 'email')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-medium transition-colors"
                  >
                    <Mail className="h-4 w-4" /> Tölvupóstur
                  </button>
                )}
              </div>
            )}

            {order.status !== 'cancelled' && order.status !== 'delivered' && (
              <button
                data-testid={`order-cancel-${order.id}`}
                onClick={() => {
                  if (window.confirm('Ertu viss um að þú viljir hætta við þessa pöntun?')) {
                    onStatusChange(order.id, 'cancelled');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium transition-colors"
              >
                <XCircle className="h-4 w-4" /> Hætta við
              </button>
            )}

            {/* Notification popup when marking as ready */}
            {showNotifyPopup && (
              <div className="absolute bottom-full left-0 mb-2 z-10">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-600 p-4 w-72">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">Merkja tilbúna</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Viltu senda tilkynningu til viðskiptavinar?</p>
                  <div className="space-y-2">
                    {cust.phone && (
                      <button
                        data-testid={`order-ready-sms-${order.id}`}
                        onClick={() => handleReadyWithNotify('sms')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 text-sm font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4" /> Senda SMS <span className="text-xs text-slate-400 ml-auto">{cust.phone}</span>
                      </button>
                    )}
                    {cust.email && (
                      <button
                        data-testid={`order-ready-email-${order.id}`}
                        onClick={() => handleReadyWithNotify('email')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        <Mail className="h-4 w-4" /> Senda tölvupóst <span className="text-xs text-slate-400 ml-auto truncate max-w-[120px]">{cust.email}</span>
                      </button>
                    )}
                    <button
                      data-testid={`order-ready-no-notify-${order.id}`}
                      onClick={() => handleReadyWithNotify(null)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Án tilkynningar
                    </button>
                    <button
                      onClick={() => setShowNotifyPopup(false)}
                      className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-1 py-1"
                    >
                      Hætta við
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});

  const token = localStorage.getItem('access_token');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchTerm) params.set('search', searchTerm);
      const res = await fetch(`${API_URL}/api/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, token]);

  // Fetch all orders for counts
  const fetchCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders?status=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const all = data.orders || [];
      const counts = { all: all.length };
      for (const s of ['new', 'processing', 'ready', 'delivered', 'cancelled']) {
        counts[s] = all.filter(o => o.status === s).length;
      }
      setStatusCounts(counts);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        fetchOrders();
        fetchCounts();
      }
    } catch (err) {
      console.error('Error updating order status:', err);
    }
  };

  const handleNotify = async (orderId, type) => {
    try {
      await fetch(`${API_URL}/api/orders/${orderId}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type }),
      });
    } catch (err) {
      console.error('Error sending notification:', err);
    }
  };

  return (
    <div data-testid="orders-management-page" className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Package className="h-6 w-6 text-blue-600" /> Pantanir
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Pantanir frá vefversluninni</p>
        </div>
        <button
          data-testid="orders-refresh-btn"
          onClick={() => { fetchOrders(); fetchCounts(); }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium transition-colors"
        >
          <RefreshCw className="h-4 w-4" /> Endurnýja
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 mb-4">
        {STATUS_TABS.map(s => {
          const count = statusCounts[s] || 0;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              data-testid={`orders-tab-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                active
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
            >
              {TAB_LABELS[s]} {count > 0 && <span className={`ml-1 ${active ? 'text-blue-200' : 'text-slate-400'}`}>({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid="orders-search-input"
          type="text"
          placeholder="Leita eftir pöntunarnúmeri, nafni, síma..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Order list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <AlertCircle className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 text-sm">Engar pantanir fundust</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              expanded={expandedId === order.id}
              onToggle={() => setExpandedId(expandedId === order.id ? null : order.id)}
              onStatusChange={handleStatusChange}
              onNotify={handleNotify}
            />
          ))}
        </div>
      )}
    </div>
  );
}
